import { memo, useCallback, useMemo } from 'react';
import { Gallery } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import Fabrics from '@Components/Fabrics';
import FabricSampleOrderingPanel from '../FabricSampleOrderingPanel';
import VideoConsulationBanner from '../../../VideoConsulationBanner';
import PreviewColor from '../PreviewColor';
import PreviewSizes from '../PreviewSizes';
import ConfiguratorTabContent from './elements/ConfiguratorTabContent';
import ConfiguratorTab from './elements/ConfiguratorTab';
import styles from './ConfiguratorTabContainer.module.css';

import type { MouseEvent } from 'react';
import type { ConfiguratorTabProps } from './elements/ConfiguratorTab';
import type {
  ConfiguratorOption,
  ConfiguratorOptionItemSize,
  OptionTheme,
} from '@Pages/PageProduct/typings';
import type { ColorData } from '@Types/Product';
import type { FiltratorId } from '@Stores/Filtrator';
import type { ChangeConfigurationProps } from '../../Sidebar';

interface Props extends Omit<ConfiguratorTabProps, 'renderSelectedSlot' | 'count' | 'productId'> {
  entityId: FiltratorId;
  needVideoConsultation?: boolean;
  canOrderSamples?: boolean;
  onClickItem?: (e: MouseEvent, item: ChangeConfigurationProps) => void;
}

interface TabFooterProps {
  needVideoConsultation?: boolean;
  canOrderSamples?: boolean;
}

interface SlotProps {
  options: ConfiguratorOption[];
  isActive: boolean;
  needGroupSelected: boolean;
  defaultSelectedTitle: string;
}

const getSelectedSlot = (props: SlotProps) => {
  const { options = [], isActive, needGroupSelected, defaultSelectedTitle } = props;
  const isTheme = (theme: OptionTheme) => options.every((option) => option.theme === theme);
  const isColor = isTheme('color');
  const isImage = isTheme('image');
  const isTabs = isTheme('tabs');
  const isColorSegment = options.every((option) =>
    ['color', 'image', 'images'].includes(option.theme),
  );
  const hasManySelectedOptions =
    options.reduce(
      (count, option) => count + option.items.filter((item) => item.selected).length,
      0,
    ) > 1;

  const selectedItems = options.flatMap((option) =>
    option.items
      .filter((item) => item.selected)
      .map((selectedParam) => {
        return {
          title: `${selectedParam.title} ${option.id === 'baseCollection' ? '' : option.title}`,
          name: selectedParam.title,
          image: selectedParam.image,
          materials: selectedParam.materials,
          collectionId: option.id,
        };
      }),
  );

  const isMultiple = selectedItems.length > 1;

  if (isColor && !isMultiple) {
    return (
      <>
        {selectedItems.map((preview) => {
          if (preview.materials) {
            const fabric = preview as unknown as ColorData;

            return (
              <div key={preview.title} className={styles.parameter}>
                <Fabrics
                  defaultSelectedFabric={fabric}
                  fabrics={[fabric]}
                  size='s'
                  view='simplified'
                />
                {preview.title && <div>{preview.title}</div>}
              </div>
            );
          }

          return <PreviewColor {...preview} key={preview.title} />;
        })}
      </>
    );
  }

  if (isImage && !isMultiple) {
    const fabric = { materials: selectedItems } as unknown as ColorData;
    const [item] = selectedItems;

    return (
      <div className={styles.parameter}>
        <Fabrics defaultSelectedFabric={fabric} fabrics={[fabric]} size='s' view='simplified' />
        <div>{item.name}</div>
      </div>
    );
  }

  if (isColorSegment) {
    const fabric = {
      materials: selectedItems.filter((item) => item.collectionId !== 'baseCollection'),
    } as unknown as ColorData;
    return <Fabrics defaultSelectedFabric={fabric} fabrics={[fabric]} size='s' view='simplified' />;
  }

  if (isTabs && needGroupSelected) {
    const titles =
      options.length > 1
        ? options
            .map((option) => option.title.charAt(0))
            .join('')
            .toUpperCase()
        : null;
    const values = options
      .flatMap((option) => option.items.find((item) => item.selected))
      .filter(Boolean) as ConfiguratorOptionItemSize[];

    return (
      <PreviewSizes
        title={titles}
        unit={values[0]?.unit}
        values={values}
        theme={isActive ? 'dark' : 'light'}
      />
    );
  }

  return (
    <div className={styles.selected}>
      {options.length > 1 || hasManySelectedOptions
        ? defaultSelectedTitle
        : options[0].items.find((item) => item.selected)?.title}
    </div>
  );
};

const TabFooter = (props: TabFooterProps) => {
  const { needVideoConsultation, canOrderSamples = true } = props;
  const { videoconsultationAvailable } = useFeatureFlags();
  const { isMobileXS, isMobileM } = useMedias();

  const needConsultation =
    videoconsultationAvailable && needVideoConsultation && isMobileM && !isMobileXS;

  if (!canOrderSamples && !needConsultation) return null;

  return (
    <Gallery cnSlide={styles.cnSlide}>
      {canOrderSamples && <FabricSampleOrderingPanel className={styles.slide} />}
      {needConsultation && <VideoConsulationBanner className={styles.slide} />}
    </Gallery>
  );
};

const ConfiguratorTabContainer = (props: Props) => {
  const { tab, entityId, needVideoConsultation, canOrderSamples, onClickItem, ...restProps } =
    props;
  const isUpholstery = tab.id === 'upholstery';
  const { t } = useTranslation();
  const isColorSegment = tab.options.every(
    (option) => option.theme === 'color' || option.theme === 'image',
  );
  const isMultiple =
    tab.options.flatMap((option) => option.items.filter((item) => item.selected))?.length > 1;

  const defaultSelectedTitle = useMemo(() => {
    const countOfChangedOptions = (tab.options || []).reduce((count, option) => {
      return count + option.items.filter((item) => item.selected).length;
    }, 0);

    const optionSpelling = t('plural-options', countOfChangedOptions);
    const selectedSpelling = t('plural-choose', countOfChangedOptions);

    return `${selectedSpelling} ${countOfChangedOptions} ${optionSpelling}`;
  }, [t, tab.options]);

  const handleCollectionClick = useCallback((collection: ConfiguratorOption) => {
    ModalWindows.open('CollectionInfo', { collection });
  }, []);

  return (
    <ConfiguratorTab
      {...restProps}
      tab={tab}
      entityId={entityId}
      slotFooter={
        isUpholstery &&
        (canOrderSamples || needVideoConsultation) && (
          <TabFooter
            canOrderSamples={canOrderSamples}
            needVideoConsultation={needVideoConsultation}
          />
        )
      }
      slotPositon={isColorSegment && isMultiple ? 'sticky' : null}
      renderSelectedSlot={(state) => {
        if (tab.id === 'modules') {
          const title = `${t('plural-choose-1', tab.selectedCount)} ${tab.selectedCount} ${t(
            'plural-modules',
            tab.selectedCount,
          )}`;
          return <div>{title}</div>;
        }

        return getSelectedSlot({
          options: tab.options,
          isActive: state,
          needGroupSelected: tab.id === 'size',
          defaultSelectedTitle,
        });
      }}
    >
      <ConfiguratorTabContent
        entityId={entityId}
        tab={tab}
        onClickCollection={handleCollectionClick}
        onClickItem={onClickItem}
      />
    </ConfiguratorTab>
  );
};

export default memo(ConfiguratorTabContainer);
